import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStoreContext } from '../../../store/store';
import { Helpers, StorageService } from '../../../utils/helpers';
import {
  LoginView,
  LoginStatus,
  LogoutReason,
  MfaValidationResult
} from '../../../utils/constants/enums';
import { useApi } from '../../../utils/hooks';
import {
  IAuthToken,
  INavigateReturnState,
  IAlertMessage,
  ILoginRequest,
  IMfaValidationRequest,
  ILoginResult,
  IMfaResult
} from '../../../utils/constants/interfaces';
import { AlertStatus } from '../../../components/ui/AlertMessage/AlertStatus';

const useLoginForm = () => {
  const { actions, settings, dispatch } = useStoreContext();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = location as INavigateReturnState;
  const queryParams = new URLSearchParams(location.search);
  const resetMfa = queryParams.get('resetmfa');
  if (resetMfa !== null) {
    StorageService.clearMfaRememberMe();
  }

  const [alertDetails, setAlertDetails] = useState<IAlertMessage>({
    visible: false,
    message: '',
    status: AlertStatus.ERROR
  });
  const [loginView, setLoginView] = useState<LoginView>(LoginView.LOGIN);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginRequest, setLoginRequest] = useState<ILoginRequest>({
    userName: '',
    password: '',
    rememberMeToken: ''
  });
  const [mfaValidationRequest, setMfaValidationRequest] = useState<IMfaValidationRequest>({
    token: '',
    mfaCode: '',
    rememberMe: false
  });

  const showAlert = (message: string, status: number) => {
    setAlertDetails((alert) => {
      return { ...alert, message, status };
    });
  };
  const clearAlert = () => {
    setAlertDetails((alert) => {
      return { ...alert, message: '' };
    });
  };

  const updateMfaRequest = (key: string, value: string) => {
    setMfaValidationRequest((req) => ({
      ...req,
      [key]: value
    }));
  };

  const toggleRememberMe = () => {
    setMfaValidationRequest((req) => ({
      ...req,
      rememberMe: !req.rememberMe
    }));
  };

  const signInUser = async (): Promise<void> => {
    if (loggingIn) {
      return;
    }
    setLoggingIn(true);
    clearAlert();

    try {
      const loginReq = { ...loginRequest };
      loginReq.rememberMeToken = StorageService.getMfaRememberMe(loginReq.userName);

      const apiResponse = await api.post<ILoginResult>('/auth/login', JSON.stringify(loginReq));
      setLoggingIn(false);
      if (!apiResponse.isSuccessStatusCode || !apiResponse.response) {
        showAlert('Failed login attempt, please try again.', AlertStatus.ERROR);
        return;
      }
      const loginResult: ILoginResult = apiResponse.response;

      //handle any error states
      switch (loginResult.status) {
        case LoginStatus.FAILED:
          showAlert('Invalid login.', AlertStatus.ERROR);
          return;
        case LoginStatus.INACTIVE:
          showAlert('Inactive account, please contact BMI.', AlertStatus.ERROR);
          return;
        case LoginStatus.LOCKED_OUT:
        case LoginStatus.MFA_LOCKED_OUT:
          showAlert(
            'Account locked for too many failed login attempts. Please try again later.',
            AlertStatus.ERROR
          );
          return;
        case LoginStatus.MFA_REQUIRED:
          if (Helpers.isNullOrWhiteSpace(loginResult.token)) {
            showAlert('Invalid login.', AlertStatus.ERROR);
            return;
          }
          updateMfaRequest('token', loginResult.token);
          setLoginView(LoginView.MFA);
          return;
        case LoginStatus.SUCCESS: {
          if (Helpers.isNullOrWhiteSpace(loginResult.token)) {
            showAlert('Invalid login.', AlertStatus.ERROR);
            return;
          }
          const authToken: IAuthToken = Helpers.decodeAuthToken(loginResult.token);
          dispatch(actions.setUserAuthToken(authToken));
          StorageService.setAuthToken(loginResult.token);

          const returnPath = returnState.state?.returnPath ?? '/';
          await navigate(returnPath);
          break;
        }
        default:
          showAlert('Unknown response from login. Please try again.', AlertStatus.ERROR);
          break;
      }
    } catch {
      showAlert('Error occurred during login, please try again.', AlertStatus.ERROR);
    } finally {
      setLoggingIn(false);
    }
  };

  const verifyMfa = async () => {
    if (loggingIn) {
      return;
    }
    setLoggingIn(true);
    clearAlert();

    try {
      const apiResponse = await api.post<IMfaResult>(
        '/auth/mfa',
        JSON.stringify(mfaValidationRequest)
      );
      setLoggingIn(false);
      updateMfaRequest('mfaCode', '');
      if (!apiResponse.isSuccessStatusCode || !apiResponse.response) {
        showAlert('Failed MFA attempt, please try again.', AlertStatus.ERROR);
        return;
      }

      const mfaResult: IMfaResult = apiResponse.response;
      //handle any error states

      switch (mfaResult.result) {
        case MfaValidationResult.FAILED:
          showAlert('Invalid code.', AlertStatus.ERROR);
          return;
        case MfaValidationResult.FAILED_INVALID_TOKEN:
          showAlert('Invalid request, please try logging in again.', AlertStatus.ERROR);
          setLoginView(LoginView.LOGIN);
          return;
        case MfaValidationResult.FAILED_CODE_EXPIRED:
          showAlert('Verification code expired, please login again.', AlertStatus.ERROR);
          setLoginView(LoginView.LOGIN);
          return;
        case MfaValidationResult.FAILED_LOCKED_OUT:
          showAlert('Too many failed attempts, account has been locked.', AlertStatus.ERROR);
          setLoginView(LoginView.LOGIN);
          return;
        case MfaValidationResult.SUCCESSFUL: {
          if (Helpers.isNullOrWhiteSpace(mfaResult.token)) {
            showAlert('Valid code, but unable to log you in. Please try again.', AlertStatus.ERROR);
            setLoginView(LoginView.LOGIN);
            return;
          }
          const authToken: IAuthToken = Helpers.decodeAuthToken(mfaResult.token);
          dispatch(actions.setUserAuthToken(authToken));
          StorageService.setAuthToken(mfaResult.token);

          if (!Helpers.isNullOrWhiteSpace(mfaResult.rememberMeToken)) {
            StorageService.setMfaRememberMe(loginRequest.userName, mfaResult.rememberMeToken);
          }
          const returnPath = returnState.state?.returnPath ?? '/';
          await navigate(returnPath);
          return;
        }
        default:
          showAlert('Unknown response from code validation. Please try again.', AlertStatus.ERROR);
          setLoginView(LoginView.LOGIN);
      }
    } catch {
      showAlert('Error occurred during mfa validation, please try again.', AlertStatus.ERROR);
      setLoginView(LoginView.LOGIN);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    await signInUser();
  };

  const handleMfaSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await verifyMfa();
  };

  const returnToLogin = () => {
    StorageService.setAuthToken('');
    dispatch(actions.setUserLoggedOut());
    setLoginView(LoginView.LOGIN);
  };

  const updateEmail = (value: string): void => {
    setLoginRequest((req) => ({ ...req, userName: value }));
    clearAlert();
  };

  const updatePassword = (value: string): void => {
    setLoginRequest((req) => ({ ...req, password: value }));
    clearAlert();
  };

  useEffect(() => {
    if (returnState.state?.reason === LogoutReason.TIMED_OUT) {
      showAlert('You have been logged out due to inactivity.', AlertStatus.ERROR);
    }
  }, [returnState.state?.reason]);

  return {
    appVersion: settings.applicationVersion,
    loggingIn,
    loginRequest,
    alertDetails,
    loginView,
    mfaValidationRequest,
    handleSubmit,
    handleMfaSubmit,
    returnToLogin,
    updateEmail,
    updatePassword,
    updateMfaRequest,
    toggleRememberMe
  };
};

export default useLoginForm;
